/**
 * Live measurement readout for an **in-progress** Terra Draw shape (ADR-034 —
 * live draw readout; the use case `measure.ts`'s header anticipated). While the
 * operator draws, this computes the running measurement off the draft geometry so
 * the drawing flow can show it on the map and update it on every click / cursor
 * move — supporting "measure the distance between two points" (line) and "size a
 * shape to a given radius / area" (circle / polygon).
 *
 * Pure (no map / no GL) so it's CI-testable; the Terra Draw lifecycle that feeds
 * it the draft feature is WebGL-only (verified in the deferred §5.11 visual pass).
 *
 * Cross-references:
 *   - ADR-034; feature-spec §6 (shapes) / §9 (units).
 *   - @/annotations/measure (the geodesic readouts) + annotation-draw-request
 *     (circleRingToCentreRadius — the draft circle's centre + radius).
 */
import type { GeoJSONStoreFeatures } from "terra-draw";
import type { DistanceUnit } from "@/auth/session-types";
import { circleMeasurement, lineMeasurement, polygonMeasurement } from "@/annotations/measure";
import { circleRingToCentreRadius } from "@/annotations/annotation-draw-request";

/** Metres per degree of latitude (WGS84 mean) — for the circle-top anchor. */
const METRES_PER_DEG_LAT = 111_320;

export interface DraftMeasurement {
  /** The formatted running readout, e.g. ``"12.3 nm · 047°T"`` or ``"R 5.0 nm"``. */
  readonly text: string;
  /** ``[lon, lat]`` where the readout should anchor on the map. */
  readonly anchor: [number, number];
}

/** ``[lon, lat]`` from a coordinate array. */
function toPair(coord: number[]): [number, number] {
  return [coord[0], coord[1]];
}

/** Drop an immediate closing duplicate so a draft ring's distinct vertices are
 *  counted correctly (Terra Draw may auto-close the in-progress polygon). */
function distinctVertices(ring: number[][]): number[][] {
  if (ring.length >= 2) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      return ring.slice(0, -1);
    }
  }
  return ring;
}

/**
 * The live readout + anchor for an in-progress Terra Draw feature, or ``null``
 * when there is nothing to measure yet (a marker, or too few vertices). Kind-aware:
 *
 *   - **line** (``linestring``, ≥2 vertices) → total distance + true azimuth;
 *     anchored at the last (cursor-following) vertex.
 *   - **circle** (``circle`` → a ``Polygon`` ring with a positive radius) → the
 *     radius readout (``R …``); anchored at the top of the circle.
 *   - **polygon** (``polygon``) → area + perimeter once it's an area (≥3 distinct
 *     vertices), else the running edge distance (2 vertices); anchored at the last
 *     vertex.
 *   - **marker / point** or any other mode → ``null`` (a single click — nothing to
 *     measure during drawing).
 */
export function draftMeasurement(
  feature: GeoJSONStoreFeatures,
  distanceUnit: DistanceUnit,
): DraftMeasurement | null {
  const mode = feature.properties.mode;
  const geometry = feature.geometry;

  if (mode === "linestring" && geometry.type === "LineString") {
    const coords = geometry.coordinates as number[][];
    if (coords.length < 2) {
      return null;
    }
    return {
      text: lineMeasurement(coords, distanceUnit).label,
      anchor: toPair(coords[coords.length - 1]),
    };
  }

  if (mode === "circle" && geometry.type === "Polygon") {
    const ring = geometry.coordinates[0] as number[][];
    if (ring.length < 3) {
      return null;
    }
    const { centre, radiusM } = circleRingToCentreRadius(ring);
    if (radiusM <= 0) {
      return null;
    }
    return {
      text: circleMeasurement(radiusM, distanceUnit).label,
      anchor: [centre[0], centre[1] + radiusM / METRES_PER_DEG_LAT], // top of the circle
    };
  }

  if (mode === "polygon" && geometry.type === "Polygon") {
    const ring = distinctVertices(geometry.coordinates[0] as number[][]);
    if (ring.length < 2) {
      return null;
    }
    const anchor = toPair(ring[ring.length - 1]);
    if (ring.length < 3) {
      // Only one edge so far — show the running distance, like a line.
      return { text: lineMeasurement(ring, distanceUnit).label, anchor };
    }
    // Close the ring for the geodesic area + perimeter.
    return { text: polygonMeasurement([...ring, ring[0]], distanceUnit).label, anchor };
  }

  return null; // marker / point or any other mode — nothing to measure mid-draw
}
